class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.blankResultPage = this.predictiveSearchResults.innerHTML;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.predictiveSearchResults.innerHTML = this.blankResultPage;
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement) && !this.predictiveSearchResults.contains(document.activeElement) ) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(newTerm) {
    this.querySelectorAll('[data-predictive-search-search-for-text]').forEach(element => {
      element.setAttribute("href", "/search?query=" + newTerm);
    })
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const productLimit = this.dataset.productLimit ? this.dataset.productLimit : 0

    const builder = new SearchRequestBuilder()
                      .withQuery(queryKey)
                      .withLimit(5);

    const [predictiveResponse, suggestionsResponse, pagesResponse, categoriesResponse] = await Promise.all([
      fetch(builder.withAttributes(['handle']).withLimit(productLimit).predict()),
      fetch(builder.suggest()),
      fetch(builder.pages()),
      fetch(builder.categories())
    ]);

    const [predictive, suggestions, pages, categories] = await Promise.all([
      predictiveResponse.json(),
      suggestionsResponse.json(),
      pagesResponse.json(),
      categoriesResponse.json()
    ])

    this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
      predictiveSearchInstance.cachedResults[queryKey] = [predictive, suggestions, pages, categories];
    });

    this.renderSearchResults([predictive, suggestions, pages, categories]);
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || document.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.textContent = statusText;

    setTimeout(() => {
    }, 1000);
  }

  async renderSearchResults([predictive, suggestions, pages, categories]) {
    this.predictiveSearchResults.innerHTML = this.blankResultPage;

    const suggestionsContainer = this.predictiveSearchResults.querySelector('[data-suggestions]');  
    this.buildSuggestions(suggestions.result).forEach(li => suggestionsContainer.appendChild(li));

    const pagesContainer = this.predictiveSearchResults.querySelector('[data-pages]');  
    this.buildPages(pages.pages).forEach(li => pagesContainer.appendChild(li));

    const categoriesContainer = this.predictiveSearchResults.querySelector('[data-categories]');  
    this.buildCategories(categories.categories).forEach(li => categoriesContainer.appendChild(li));

    const predictiveContainer = this.predictiveSearchResults.querySelector('[data-products]');
    console.log("await this.buildPredictive(predictive)", await this.buildPredictive(predictive))
    predictiveContainer.innerHTML = await this.buildPredictive(predictive);

    this.predictiveSearchResults.querySelector('[data-query]').textContent = predictive.query;


    this.updateSearchForTerm(predictive.query);
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  async buildPredictive(predictive) {
    const items = predictive.product_data;

    const response = await fetch(SearchPage.getSectionRenderUrl(items, 'predictive'));
    const responseText = await response.text();

    const html = new DOMParser().parseFromString(responseText, "text/html");
    const productCards = html.querySelectorAll('product-card');
    
    let result = '';
    productCards.forEach(card => {
        result += card.outerHTML;
    });

    return result;
}

  buildSuggestions(suggestions) {
    const list = [];
    for (let suggestion of suggestions) {
      const li = document.createElement('li');
      const href = document.createElement('a');
      href.setAttribute('href', "/search/?query=" + suggestion);
      href.textContent = suggestion;
      li.append(href);
      list.push(li);
    }
    return list;
  }

  buildPages(pages) {
    const list = [];
    for (let page of pages) {
      const li = document.createElement('li');
      const href = document.createElement('a');
      href.setAttribute('href', page.url);
      href.textContent = page.title;
      li.append(href);
      list.push(li);
    }
    return list;
  }

  buildCategories(categories) {
    const list = [];
    for (let category of categories) {
      const li = document.createElement('li');
      const href = document.createElement('a');
      href.setAttribute('href', category?.url);
      href.textContent = category?.name;
      li.append(href);
      list.push(li);
    }
    return list;
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.predictiveSearchResults.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  open() {
    this.predictiveSearchResults.dataset.open = true;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    this.predictiveSearchResults.dataset.open = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
